/**
 * One write in flight per share dialog (the queue dialogs' `useQueueDialogOperation` pattern): `begin` records the
 * handle, `settle` tells whether an answer still belongs to the open dialog, and closing (or unmounting) while it runs
 * cancels that request in main and records the action ONCE as `cancelled` — a late answer then changes nothing.
 */
import { useCallback, useEffect, useRef } from 'react';
import { trackShareAction, type ShareAction, type ShareActionMeta } from '../shareActions';

type Operation = { handle: { cancel: () => void }; action: ShareAction; meta: ShareActionMeta };

export function useShareOperation() {
  const active = useRef<Operation | null>(null);
  const begin = useCallback((handle: Operation['handle'], action: ShareAction, meta: ShareActionMeta) => {
    active.current = { handle, action, meta };
  }, []);
  const settle = useCallback((handle: Operation['handle']): boolean => {
    if (active.current?.handle !== handle) return false;
    active.current = null;
    return true;
  }, []);
  const abandon = useCallback(() => {
    const operation = active.current;
    if (!operation) return;
    active.current = null;
    operation.handle.cancel();
    trackShareAction(operation.action, 'cancelled', operation.meta);
  }, []);
  useEffect(() => abandon, [abandon]);
  return { begin, settle, abandon, isActive: () => active.current !== null };
}
